import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Home,
  Users,
  Factory,
  Map,
  Briefcase,
  BookOpen,
  BarChart2,
  Info,
  LogOut,
  Menu,
  X,
  ChevronRight,
  MessageSquare,
  ShieldAlert,
  HelpCircle,
  ClipboardList,
  UserCheck,
  Megaphone
} from 'lucide-react';
import { NotificationDropdown } from './NotificationDropdown';
import { SubmitConcernModal } from '../modals/SubmitConcernModal';

const BASE_NAV_ITEMS = [
  { path: '/home',          label: 'Home',          Icon: Home      },
  { path: '/announcements', label: 'Announcements', Icon: Megaphone },
  { path: '/farmers',       label: 'Farmers',       Icon: Users     },
  { path: '/processors',    label: 'Processors',    Icon: Factory   },
  { path: '/map',           label: 'Map',           Icon: Map       },
  { path: '/services',      label: 'Services',      Icon: Briefcase },
  { path: '/research',      label: 'Research',      Icon: BookOpen  },
  { path: '/analytics',     label: 'Analytics',     Icon: BarChart2 },
  { path: '/about',         label: 'About',         Icon: Info      },
];

const BG_GRADIENT = 'linear-gradient(180deg, #451a03 0%, #78350f 40%, #92400e 70%, #78350f 100%)';

function getInitials(user) {
  if (user.firstName && user.lastName) {
    return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
  }
  if (user.username) return user.username.substring(0, 2).toUpperCase();
  return 'U';
}

function getDisplayName(user) {
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
  return user.username || 'User';
}

export function Sidebar({ user, onLogout, portalLabel, badgeBg, onCollapseChange }) {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showConcernModal, setShowConcernModal] = useState(false);
  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    onCollapseChange?.(next);
  };

  const handleLogout = () => {
    setMobileOpen(false);
    onLogout();
    navigate('/');
  };

  const handleProfile = () => {
    setMobileOpen(false);
    navigate('/profile');
  };

  const computedPortalLabel = portalLabel || (
    user?.role === 'admin' ? 'Admin Portal' :
    user?.role === 'farmer' ? 'Farmer Portal' :
    user?.role === 'processor' ? 'Processor Portal' : 'User Portal'
  );

  const canAccessMessaging = user?.role === 'admin' || user?.role === 'farmer' || user?.role === 'processor';

  const navItems = [
    ...BASE_NAV_ITEMS.slice(0, 4),
    ...(canAccessMessaging ? [{ path: '/messages', label: 'Messages', Icon: MessageSquare }] : []),
    ...BASE_NAV_ITEMS.slice(4)
  ];

  /* ── Inner nav + bottom section ── */
  const SidebarContent = ({ isDrawer = false }) => {
    const isIconOnly = collapsed && !isDrawer;

    return (
      <div className="flex flex-col h-full overflow-hidden">

        {/* ── Nav links ─────────────────────────────────────────── */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto overflow-x-hidden scrollbar-none">
          {navItems.map(({ path, label, Icon }) => (
            <NavLink
              key={path}
              to={path}
              onClick={() => setMobileOpen(false)}
              title={isIconOnly ? label : ''}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium
                 transition-all duration-200 group whitespace-nowrap
                 ${isIconOnly ? 'justify-center' : ''}
                 ${isActive
                   ? 'bg-amber-500 text-white shadow-lg shadow-amber-900/40'
                   : 'text-amber-100 hover:bg-amber-700/50 hover:text-white'
                 }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={`w-5 h-5 shrink-0 transition-colors
                      ${isActive ? 'text-white' : 'text-amber-300 group-hover:text-amber-100'}`}
                  />
                  {!isIconOnly && (
                    <>
                      <span className="flex-1 leading-none">{label}</span>
                      {isActive && (
                        <span className="ml-auto w-1.5 h-1.5 rounded-full bg-white shrink-0" />
                      )}
                    </>
                  )}
                </>
              )}
            </NavLink>
          ))}

          {/* Admin Management Links */}
          {user?.role === 'admin' ? (
            <div className="pt-2 mt-2 border-t border-amber-600/40 space-y-1">
              <NavLink
                to="/admin/profile-requests"
                onClick={() => setMobileOpen(false)}
                title={isIconOnly ? 'Profile Requests' : ''}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
                   transition-all duration-200 group whitespace-nowrap
                   ${isIconOnly ? 'justify-center' : ''}
                   ${isActive ? 'bg-amber-600 text-white shadow-lg' : 'text-amber-100 hover:bg-amber-700/50'}`
                }
              >
                <ClipboardList className="w-5 h-5 text-amber-300 shrink-0" />
                {!isIconOnly && <span className="flex-1 leading-none">Profile Requests</span>}
              </NavLink>

              <NavLink
                to="/admin/applications"
                onClick={() => setMobileOpen(false)}
                title={isIconOnly ? 'User Applications' : ''}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
                   transition-all duration-200 group whitespace-nowrap
                   ${isIconOnly ? 'justify-center' : ''}
                   ${isActive ? 'bg-amber-600 text-white shadow-lg' : 'text-amber-100 hover:bg-amber-700/50'}`
                }
              >
                <UserCheck className="w-5 h-5 text-amber-300 shrink-0" />
                {!isIconOnly && <span className="flex-1 leading-none">User Applications</span>}
              </NavLink>

              <NavLink
                to="/admin/concerns"
                onClick={() => setMobileOpen(false)}
                title={isIconOnly ? 'Support Tickets' : ''}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
                   transition-all duration-200 group whitespace-nowrap
                   ${isIconOnly ? 'justify-center' : ''}
                   ${isActive ? 'bg-amber-600 text-white shadow-lg' : 'text-amber-100 hover:bg-amber-700/50'}`
                }
              >
                <ShieldAlert className="w-5 h-5 text-amber-300 shrink-0" />
                {!isIconOnly && <span className="flex-1 leading-none">Support Tickets</span>}
              </NavLink>
            </div>
          ) : (
            <button
              onClick={() => {
                setMobileOpen(false);
                setShowConcernModal(true);
              }}
              title={isIconOnly ? 'Raise Concern' : ''}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium
                 transition-all duration-200 group whitespace-nowrap mt-2 bg-gradient-to-r from-amber-600/40 to-amber-700/40 hover:from-amber-600 hover:to-amber-700 text-amber-100 border border-amber-500/40
                 ${isIconOnly ? 'justify-center' : ''}`}
            >
              <HelpCircle className="w-5 h-5 text-amber-200 shrink-0" />
              {!isIconOnly && <span className="flex-1 leading-none text-left font-semibold">Raise Concern to Admin</span>}
            </button>
          )}
        </nav>

        {/* ── Profile + Logout ──────────────────────────────────── */}
        <div className={`px-2 pb-5 pt-2 border-t border-amber-700/50 space-y-1
          ${isIconOnly ? 'flex flex-col items-center' : ''}`}
        >
          {/* Profile */}
          <button
            onClick={handleProfile}
            title={isIconOnly ? getDisplayName(user) : ''}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm
              font-medium transition-all duration-200 group text-left
              hover:bg-amber-700/50
              ${isIconOnly ? 'justify-center' : ''}`}
          >
            {/* Avatar */}
            <div className="shrink-0">
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt="Profile"
                  className="w-8 h-8 rounded-full object-cover border-2 border-amber-300 shadow-sm"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-300 to-amber-500
                  flex items-center justify-center text-amber-950 text-sm font-extrabold
                  border-2 border-amber-300 shadow-sm">
                  {getInitials(user)}
                </div>
              )}
            </div>

            {/* Name & badge */}
            {!isIconOnly && (
              <>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate
                    group-hover:text-amber-200 transition-colors">
                    {getDisplayName(user)}
                  </p>
                  <span className={`inline-block mt-0.5 px-2 py-0.5 text-xs font-semibold
                    rounded-full text-white shadow-sm capitalize ${badgeBg}`}>
                    {user.role || 'User'}
                  </span>
                </div>
                <ChevronRight className="w-4 h-4 text-amber-400 shrink-0
                  group-hover:translate-x-0.5 transition-transform" />
              </>
            )}
          </button>

          {/* Divider */}
          <div className="border-t border-amber-700/40 my-1" />

          {/* Logout */}
          <button
            onClick={handleLogout}
            title={isIconOnly ? 'Logout' : ''}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm
              font-semibold text-red-300 hover:bg-red-500/20 hover:text-red-200
              transition-all duration-200 group
              ${isIconOnly ? 'justify-center' : ''}`}
          >
            <LogOut className="w-5 h-5 shrink-0 group-hover:translate-x-0.5 transition-transform" />
            {!isIconOnly && <span>Logout</span>}
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* DESKTOP SIDEBAR */}
      <aside
        className={`hidden md:flex flex-col fixed top-0 left-0 h-screen z-40 shadow-2xl
          transition-all duration-300 ease-in-out
          ${collapsed ? 'w-16' : 'w-64'}`}
        style={{ background: BG_GRADIENT }}
      >
        {/* Brand strip with Notification Bell */}
        <div className="w-full px-3 pt-4 pb-3 border-b border-amber-700/40 flex items-center justify-between">
          <button
            onClick={toggleCollapse}
            className="flex items-center gap-2 text-left focus:outline-none group"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <img
              src="/bc_logo.png"
              alt="BCIIS Logo"
              className={`object-contain transition-all duration-300 transform group-hover:scale-105
                ${collapsed ? 'w-8 h-8' : 'w-10 h-10'}`}
            />
            {!collapsed && (
              <div>
                <p className="text-white text-base font-extrabold tracking-wide leading-none group-hover:text-amber-200 transition-colors">
                  BCIIS
                </p>
                <p className="text-amber-300 text-[10px] font-medium leading-none tracking-widest uppercase mt-0.5">
                  {computedPortalLabel}
                </p>
              </div>
            )}
          </button>
        </div>

        <SidebarContent />
      </aside>

      {/* MOBILE TOP BAR */}
      <div
        className="md:hidden fixed top-0 left-0 right-0 z-50 flex items-center
          justify-between px-4 h-14 shadow-lg"
        style={{ background: '#451a03' }}
      >
        <div className="flex items-center gap-2">
          <img src="/bc_logo.png" alt="BCIIS Logo" className="w-8 h-8 object-contain" />
          <span className="text-white text-sm font-bold">BCIIS</span>
        </div>
        <div className="flex items-center gap-2">
          <NotificationDropdown user={user} />
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-lg text-amber-200 hover:bg-amber-800 transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 flex"
          style={{ backdropFilter: 'blur(2px)', backgroundColor: 'rgba(0,0,0,0.55)' }}
          onClick={() => setMobileOpen(false)}
        >
          <div
            className="w-72 h-full shadow-2xl flex flex-col"
            style={{ background: BG_GRADIENT }}
            onClick={e => e.stopPropagation()}
          >
            <div className="px-4 pt-4 pb-3 flex items-center justify-between border-b border-amber-700/40">
              <div className="flex items-center gap-2">
                <img src="/bc_logo.png" alt="BCIIS Logo" className="w-8 h-8 object-contain" />
                <div>
                  <p className="text-white text-sm font-bold leading-none">BCIIS</p>
                  <p className="text-amber-300 text-[10px] uppercase tracking-widest leading-none mt-0.5">
                    {computedPortalLabel}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-1.5 rounded-lg text-amber-200 hover:bg-amber-800 transition-colors"
                aria-label="Close menu"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <SidebarContent isDrawer={true} />
          </div>
        </div>
      )}

      {/* Concern Modal */}
      {showConcernModal && user && (
        <SubmitConcernModal
          user={user}
          onClose={() => setShowConcernModal(false)}
          onSuccess={() => setShowConcernModal(false)}
        />
      )}
    </>
  );
}

Sidebar.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    username: PropTypes.string,
    avatar: PropTypes.string,
    role: PropTypes.string,
  }).isRequired,
  onLogout: PropTypes.func.isRequired,
  portalLabel: PropTypes.string.isRequired,
  badgeBg: PropTypes.string.isRequired,
  onCollapseChange: PropTypes.func,
};
